import { useMemo, useRef } from 'react';

/**
 * Builds an `onValuesChange` handler for SchemaForm that reports whether the
 * form still matches its seed. A host modal uses it to refuse a stray Escape or
 * backdrop click instead of silently binning what the user typed.
 *
 * Returns undefined when no consumer asked, so SchemaForm's change effect stays
 * a no-op for the forms that don't need it.
 */
export function useValuesDirty(
  initialValues: Record<string, unknown> | undefined,
  onDirtyChange?: (dirty: boolean) => void,
): ((values: Record<string, unknown>) => void) | undefined {
  const baseline = useMemo(() => JSON.stringify(initialValues ?? {}), [initialValues]);
  const lastRef = useRef<boolean | null>(null);
  const cbRef = useRef(onDirtyChange);
  cbRef.current = onDirtyChange;

  return useMemo(() => {
    if (!onDirtyChange) return undefined;
    return (values: Record<string, unknown>) => {
      const dirty = JSON.stringify(values) !== baseline;
      if (dirty === lastRef.current) return;
      lastRef.current = dirty;
      cbRef.current?.(dirty);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseline, !!onDirtyChange]);
}
